import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  GripVertical,
} from 'lucide-react';
import { GameObject, GAMEOBJECT_ICONS } from '../../../core/types/gameobject.types';
import { HierarchyDragState } from '../../../core/types/hierarchy.types';
import { useSceneStore } from '../../store/sceneStore';
import { cn } from '../../utils/cn';

interface HierarchyNodeProps {
  gameObject: GameObject;
  depth: number;
  searchQuery: string;
  dragState: HierarchyDragState | null;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDragOver: (id: string, position: 'before' | 'after' | 'inside') => void;
  onDrop: (e: React.DragEvent) => void;
  onContextMenu: (e: React.MouseEvent, id: string) => void;
  renamingId: string | null;
  onRenameStart: (id: string) => void;
  onRenameEnd: () => void;
  parentVisible?: boolean; // Whether all ancestors are visible
  parentLocked?: boolean; // Whether any ancestor is locked
}

export const HierarchyNode: React.FC<HierarchyNodeProps> = ({
  gameObject,
  depth,
  searchQuery,
  dragState,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onContextMenu,
  renamingId,
  onRenameStart,
  onRenameEnd,
  parentVisible = true,
  parentLocked = false,
}) => {
  const {
    gameObjects,
    selectedIds,
    select,
    toggleExpanded,
    toggleVisibility,
    toggleLock,
    updateGameObject,
  } = useSceneStore();

  const [localName, setLocalName] = useState(gameObject.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  const isSelected = selectedIds.includes(gameObject.id);
  const isExpanded = gameObject.expanded;
  const hasChildren = gameObject.childIds.length > 0;
  const isRenaming = renamingId === gameObject.id;
  const isDragging = dragState?.draggedId === gameObject.id;
  const isDragTarget = dragState?.targetId === gameObject.id && !isDragging;

  // Determine effective visibility: node is effectively hidden if it OR any ancestor is hidden
  const isEffectivelyVisible = parentVisible && gameObject.visible;
  // Track if this node is hidden due to an ancestor (not itself)
  const isInheritedHidden = !parentVisible && gameObject.visible;

  // Determine effective lock: node is effectively locked if it OR any ancestor is locked
  const isEffectivelyLocked = parentLocked || gameObject.locked;
  // Track if this node is locked due to an ancestor (not itself)
  const isInheritedLocked = parentLocked && !gameObject.locked;

  // Auto-scroll into view when selected
  useEffect(() => {
    if (isSelected && nodeRef.current) {
      nodeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isSelected]);

  // Focus input when renaming starts
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      setLocalName(gameObject.name);
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming, gameObject.name]);

  // Handle rename submission
  const handleRenameSubmit = useCallback(() => {
    const trimmedName = localName.trim();
    if (trimmedName && trimmedName !== gameObject.name) {
      updateGameObject(gameObject.id, { name: trimmedName });
    }
    onRenameEnd();
  }, [localName, gameObject.id, gameObject.name, updateGameObject, onRenameEnd]);

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      setLocalName(gameObject.name);
      onRenameEnd();
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const isMultiSelect = e.ctrlKey || e.metaKey;
    select(gameObject.id, isMultiSelect);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEffectivelyLocked) {
      onRenameStart(gameObject.id);
    }
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleExpanded(gameObject.id);
  };

  const handleToggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleVisibility(gameObject.id);
  };

  const handleToggleLock = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleLock(gameObject.id);
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (isEffectivelyLocked) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', gameObject.id);
    onDragStart(gameObject.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!nodeRef.current || isDragging) return;

    const rect = nodeRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;

    let position: 'before' | 'after' | 'inside';

    if (y < height * 0.25) {
      position = 'before';
    } else if (y > height * 0.75) {
      position = 'after';
    } else {
      position = 'inside';
    }

    onDragOver(gameObject.id, position);
  };

  const handleDragEnd = () => {
    onDragEnd();
  };

  const handleDropEvent = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDrop(e);
  };

  const handleContextMenuEvent = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu(e, gameObject.id);
  };

  // Filter children based on search (deduplicate to avoid React key warnings)
  const visibleChildren = [...new Set(gameObject.childIds)].filter((childId) => {
    const child = gameObjects[childId];
    if (!child) return false;
    if (!searchQuery) return true;
    return child.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Render children recursively
  const renderChildren = () => {
    if (!isExpanded || visibleChildren.length === 0) return null;

    return (
      <div className="ml-0">
        {visibleChildren.map((childId) => {
          const child = gameObjects[childId];
          if (!child) return null;
          return (
            <HierarchyNode
              key={childId}
              gameObject={child}
              depth={depth + 1}
              searchQuery={searchQuery}
              dragState={dragState}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onContextMenu={onContextMenu}
              renamingId={renamingId}
              onRenameStart={onRenameStart}
              onRenameEnd={onRenameEnd}
              parentVisible={isEffectivelyVisible}
              parentLocked={isEffectivelyLocked}
            />
          );
        })}
      </div>
    );
  };

  // Get drop indicator styles
  const getDropIndicator = () => {
    if (!isDragTarget || !dragState) return null;

    const baseClass = 'absolute left-0 right-0 h-0.5 bg-blue-500 pointer-events-none';

    switch (dragState.position) {
      case 'before':
        return <div className={cn(baseClass, 'top-0')} />;
      case 'after':
        return <div className={cn(baseClass, 'bottom-0')} />;
      case 'inside':
        return (
          <div className="absolute inset-0 border-2 border-blue-500 rounded pointer-events-none" />
        );
      default:
        return null;
    }
  };

  return (
    <div className="select-none">
      <div
        ref={nodeRef}
        className={cn(
          'relative flex items-center gap-1 px-1 py-0.5 cursor-pointer group transition-colors',
          isSelected
            ? 'bg-blue-600/30 hover:bg-blue-600/40'
            : 'hover:bg-zinc-800',
          isDragging && 'opacity-50',
          !isEffectivelyVisible && 'opacity-50',
          isInheritedHidden && 'opacity-30', // Extra dim for inherited hidden state
          isEffectivelyLocked && 'cursor-not-allowed'
        )}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenuEvent}
        draggable={!isEffectivelyLocked && !isRenaming}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDrop={handleDropEvent}
      >
        {/* Drop indicator */}
        {getDropIndicator()}

        {/* Drag handle */}
        <span className="opacity-0 group-hover:opacity-50 cursor-grab">
          <GripVertical className="w-3 h-3 text-zinc-500" />
        </span>

        {/* Expand/collapse chevron */}
        <button
          onClick={handleToggleExpand}
          className={cn(
            'w-4 h-4 flex items-center justify-center',
            !hasChildren && 'invisible'
          )}
        >
          {isExpanded ? (
            <ChevronDown className="w-3 h-3 text-zinc-400" />
          ) : (
            <ChevronRight className="w-3 h-3 text-zinc-400" />
          )}
        </button>

        {/* Type icon */}
        <span className="text-sm" title={gameObject.type}>
          {GAMEOBJECT_ICONS[gameObject.type]}
        </span>

        {/* Selection indicator */}
        {isSelected && (
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
        )}

        {/* Name or rename input */}
        {isRenaming ? (
          <input
            ref={inputRef}
            type="text"
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleRenameKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-w-0 px-1 py-0 text-sm bg-zinc-700 border border-blue-500 rounded text-zinc-200 focus:outline-none"
          />
        ) : (
          <span
            className={cn(
              'flex-1 min-w-0 text-sm truncate',
              isSelected ? 'text-blue-200' : 'text-zinc-300',
              !gameObject.active && 'italic text-zinc-500'
            )}
          >
            {gameObject.name}
          </span>
        )}

        {/* Visibility toggle */}
        <button
          onClick={handleToggleVisibility}
          className={cn(
            'p-0.5 opacity-0 group-hover:opacity-100 hover:bg-zinc-700 rounded transition-opacity',
            !gameObject.visible && 'opacity-100'
          )}
          title={gameObject.visible ? 'Hide' : 'Show'}
        >
          {gameObject.visible ? (
            <Eye className="w-3.5 h-3.5 text-zinc-400" />
          ) : (
            <EyeOff className="w-3.5 h-3.5 text-amber-500" />
          )}
        </button>

        {/* Lock toggle */}
        <button
          onClick={handleToggleLock}
          className={cn(
            'p-0.5 opacity-0 group-hover:opacity-100 hover:bg-zinc-700 rounded transition-opacity',
            isEffectivelyLocked && 'opacity-100'
          )}
          title={isInheritedLocked ? 'Locked by parent' : (gameObject.locked ? 'Unlock' : 'Lock')}
        >
          {isEffectivelyLocked ? (
            <Lock className={cn('w-3.5 h-3.5', isInheritedLocked ? 'text-amber-500/60' : 'text-amber-500')} />
          ) : (
            <Unlock className="w-3.5 h-3.5 text-zinc-400" />
          )}
        </button>
      </div>

      {/* Children */}
      {renderChildren()}
    </div>
  );
};
